import { isThreadSettled } from './shared-responder-v2-core-follow-up-reply.helpers';
import { resolveEffectiveInteractionType } from './shared-responder-v2-core-interaction.helpers';
import {
  computeVisibleUpperBound,
  hasBranchingBarrier,
  resolveProgressVisibility,
} from './shared-responder-v2-core-reachability.helpers';
import {
  PROGRESS_VISIBILITIES,
  RESPONDER_PHASES,
  RESPONSE_MODES,
  STEP_AFFORDANCES,
  STEP_STATUSES,
  type ProgressVisibility,
  type ResponderPhase,
  type ResponseMode,
  type StepAffordance,
  type StepStatus,
} from './shared-responder-v2-core-session.consts';
import type {
  ResponderSessionState,
  ResponderStep,
} from './shared-responder-v2-core-session.types';
import {
  isBlockedBySiteGate,
  isHostPageSite,
  resolveTaskSiteUrl,
} from './shared-responder-v2-core-site-gate.helpers';
import { getThreadBehaviour } from './shared-responder-v2-core-thread-registry';
import {
  IN_FLIGHT_THREAD_STATUSES,
  THREAD_KINDS,
  THREAD_STATUSES,
} from './shared-responder-v2-core-thread.consts';
import type {
  QuestionSpec,
  ResponderThread,
} from './shared-responder-v2-core-thread.types';

export function selectCurrentStep(
  state: ResponderSessionState
): ResponderStep | null {
  return state.steps[state.currentStepIndex] ?? null;
}

const TERMINAL_PHASES: ReadonlySet<string> = new Set([
  RESPONDER_PHASES.Outro,
  RESPONDER_PHASES.ScreenedOut,
  RESPONDER_PHASES.SessionError,
]);

/** A phase the session never leaves: nothing after one of these can be answered. */
export function isTerminalResponderPhase(phase: ResponderPhase): boolean {
  return TERMINAL_PHASES.has(phase);
}

export function isThreadInFlight(thread: ResponderThread | null): boolean {
  return thread !== null && IN_FLIGHT_THREAD_STATUSES.has(thread.status);
}

export function hasInFlightThread(state: ResponderSessionState): boolean {
  return Object.values(state.threads).some(isThreadInFlight);
}

export function selectCurrentThread(
  state: ResponderSessionState
): ResponderThread | null {
  const step = selectCurrentStep(state);

  return step === null ? null : (state.threads[step.questionId] ?? null);
}

export function selectCurrentSpec(
  state: ResponderSessionState
): QuestionSpec | null {
  const step = selectCurrentStep(state);

  return step === null ? null : (state.specs[step.questionId] ?? null);
}

export function selectEffectiveSiteUrl(
  state: ResponderSessionState
): string | null {
  const spec = selectCurrentSpec(state);

  return spec === null
    ? null
    : resolveTaskSiteUrl(
        spec.contentUrl,
        state.lastOpenedSiteUrl,
        state.capabilities.hostPageUrl ?? null
      );
}

export interface ResponderTaskSummary {
  readonly count: number;
  readonly opensExternalSite: boolean;
}

export function selectTaskSummary(
  state: ResponderSessionState
): ResponderTaskSummary {
  const hostPageUrl = state.capabilities.hostPageUrl ?? null;

  let carriedUrl: string | null = null;
  let count = 0;
  let opensExternalSite = false;

  for (const step of state.steps) {
    const spec = state.specs[step.questionId];

    if (spec !== undefined && getThreadBehaviour(spec.kind).ownsContentUrl) {
      const siteUrl = resolveTaskSiteUrl(
        spec.contentUrl,
        carriedUrl,
        hostPageUrl
      );

      count += 1;
      opensExternalSite ||= siteUrl !== null && siteUrl !== hostPageUrl;
      carriedUrl = siteUrl;
    }
  }

  return { count, opensExternalSite };
}

export function selectIsHostPageSite(state: ResponderSessionState): boolean {
  return isHostPageSite(
    selectEffectiveSiteUrl(state),
    state.capabilities.hostPageUrl ?? null
  );
}

export function selectIsBlockedBySiteGate(
  state: ResponderSessionState
): boolean {
  return isBlockedBySiteGate(
    selectCurrentSpec(state),
    selectCurrentThread(state),
    selectEffectiveSiteUrl(state)
  );
}

export function selectMobileAppEmbedUrl(
  state: ResponderSessionState
): string | null {
  return state.mobileAppEmbedUrl;
}

const MOBILE_APP_EMBED_STEP_KINDS: ReadonlySet<string> = new Set([
  THREAD_KINDS.MobileApp,
  THREAD_KINDS.Task,
]);

export function selectShowsMobileAppEmbed(
  state: ResponderSessionState
): boolean {
  if (
    state.mobileAppEmbedUrl === null ||
    state.phase !== RESPONDER_PHASES.Question
  ) {
    return false;
  }

  const step = selectCurrentStep(state);

  return step !== null && MOBILE_APP_EMBED_STEP_KINDS.has(step.kind);
}

export function selectProgressVisibility(
  state: ResponderSessionState
): ProgressVisibility {
  return resolveProgressVisibility({
    hasUrlOrTask: state.capabilities.hasUrlOrTask,
    canJumpForward: state.capabilities.canJumpForward,
    hasBranchingBarrier: hasBranchingBarrier({
      steps: state.steps,
      maxReachedStepIndex: state.maxReachedStepIndex,
    }),
  });
}

/**
 * Steps for the progress list, sliced to the *visibility* bound, not the jump bound — a step
 * past the reachable bound still renders, and `isStepReachable` is what makes it unclickable.
 */
export function selectVisibleSteps(
  state: ResponderSessionState
): readonly ResponderStep[] {
  if (selectProgressVisibility(state) === PROGRESS_VISIBILITIES.Hidden) {
    return [];
  }

  const bound = computeVisibleUpperBound({
    steps: state.steps,
    maxReachedStepIndex: state.maxReachedStepIndex,
    isComplete: state.isComplete,
  });

  return state.steps.slice(0, bound + 1);
}

/**
 * Whether the rail may draw a step as finished. `submitAnswer` writes `thread.answer`
 * optimistically at submit time, so a `Failed` thread returns false even though `answer` is
 * kept (for `onRetryTurn` to re-issue) — otherwise a rejected turn would show a permanent
 * green tick. A resumed thread has no typed `answer`, so its `Closed` status alone counts as done.
 */
export function selectIsStepAnswered(
  state: ResponderSessionState,
  questionId: string
): boolean {
  const thread = state.threads[questionId];

  if (thread === undefined || thread.status === THREAD_STATUSES.Failed) {
    return false;
  }

  return thread.answer != null || thread.status === THREAD_STATUSES.Closed;
}

/**
 * Maps to the progress-list states (Done, Active, Current, Upcoming, Skipped). `Active` is the
 * on-screen step; `Current` is the resume point — where Continue last landed, which a rail jump
 * never moves — shown while looking away from it unless its question is already completed, so a
 * step left mid follow-up reads `Current` rather than `Done`.
 *
 * `currentStepIndex` keeps pointing at a question through the Intro and Outro phases, so without
 * the phase check a question claims `Active` — and `aria-current` — while the responder is looking
 * at the intro or the thank-you screen.
 */
export function selectStepStatus(
  state: ResponderSessionState,
  index: number
): StepStatus {
  const step = state.steps[index];
  const isAnswered =
    step !== undefined && selectIsStepAnswered(state, step.questionId);

  if (state.phase === RESPONDER_PHASES.Outro) {
    return isAnswered ? STEP_STATUSES.Done : STEP_STATUSES.Skipped;
  }

  if (state.phase !== RESPONDER_PHASES.Question) {
    return isAnswered ? STEP_STATUSES.Done : STEP_STATUSES.Upcoming;
  }

  if (index === state.currentStepIndex) {
    return STEP_STATUSES.Active;
  }

  if (
    index === state.resumeStepIndex &&
    step !== undefined &&
    state.threads[step.questionId]?.status !== THREAD_STATUSES.Closed
  ) {
    return STEP_STATUSES.Current;
  }

  if (isAnswered) {
    return STEP_STATUSES.Done;
  }

  if (
    step !== undefined &&
    state.skippedQuestionIds.includes(step.questionId)
  ) {
    return STEP_STATUSES.Skipped;
  }

  return STEP_STATUSES.Upcoming;
}

export function selectStepAffordance(
  state: ResponderSessionState,
  index: number
): StepAffordance {
  const status = selectStepStatus(state, index);

  return status === STEP_STATUSES.Skipped ? STEP_AFFORDANCES.Upcoming : status;
}

export function selectRemainingStepCount(state: ResponderSessionState): number {
  const lastVisibleIndex = computeVisibleUpperBound({
    steps: state.steps,
    maxReachedStepIndex: state.maxReachedStepIndex,
    isComplete: state.isComplete,
  });

  return state.steps.slice(0, lastVisibleIndex + 1).filter((_step, index) => {
    const status = selectStepStatus(state, index);

    return status !== STEP_STATUSES.Done && status !== STEP_STATUSES.Skipped;
  }).length;
}

export function selectEffectiveMode(
  state: ResponderSessionState
): ResponseMode {
  const spec = selectCurrentSpec(state);

  if (spec === null) {
    return RESPONSE_MODES.Text;
  }

  return resolveEffectiveInteractionType({
    projectInteractionType: state.capabilities.projectInteractionType,
    questionInteractionType: spec.interactionType,
    hasUrlOrTask: state.capabilities.hasUrlOrTask,
    kind: spec.kind,
    allowVoice: state.capabilities.allowVoice,
  });
}

/**
 * Whether the current question is closed with nothing owed; composer stands down and Continue
 * takes the slot so exactly one control is live. See `isThreadSettled` for why `Closed` alone isn't enough.
 */
export function selectIsCurrentQuestionSettled(
  state: ResponderSessionState
): boolean {
  const thread = selectCurrentThread(state);

  return thread !== null && isThreadSettled(state, thread.questionId);
}

export function selectShowsContinueButton(
  state: ResponderSessionState
): boolean {
  const spec = selectCurrentSpec(state);

  if (spec === null) {
    return false;
  }

  // Settled question always offers Continue ahead of per-kind rules; otherwise a kind whose
  // `showsContinueButton` defaults to `Text && !isRequired` (e.g. required Contact Info) is stranded.
  if (selectIsCurrentQuestionSettled(state)) {
    return true;
  }

  const mode = selectEffectiveMode(state);
  const behaviour = getThreadBehaviour(spec.kind);

  if (behaviour.showsContinueButton(spec, mode)) {
    return true;
  }

  if (!behaviour.advancesOnSurfacePick) {
    return false;
  }

  if (mode !== RESPONSE_MODES.Voice && !isAutoAdvanceBlocked(state)) {
    return false;
  }

  const thread = selectCurrentThread(state);

  if (thread === null) {
    return false;
  }

  return (
    thread.answer !== null || behaviour.isDraftComplete(thread.draft, spec)
  );
}

/**
 * States `selectShouldAutoAdvance` refuses to advance out of, such as once the session has finished.
 */
function isAutoAdvanceBlocked(state: ResponderSessionState): boolean {
  return state.isComplete;
}
